import type { SeasonCalendarType } from '@/features/season-calendar/season-calendar-type';

import { Columns } from '@/core/features';
import color from '@/core/features/styles/color';
import { toTwoDigits } from '@/core/utils/number-util';

export type SeasonCalendarProps = SeasonCalendarType & {
    fontColor?: 'inherit' | 'white';
    gapOverride?: 3 | 4;
    hideCalendarTitle?: boolean;
    monthFontSize?: 'text-12' | 'text-14';
    rowCount?: 1 | 2 | 3;
    shadow?: boolean;
    titleFontSize?: 'text-12' | 'text-14' | 'text-16';
    titleLineHeight?: 'leading-12' | 'leading-17';
    titleMarginBottom?: number;
    titleTruncation?: 'ellipsis' | 'text-nowrap';
};

export default function SeasonCalendar({
    fontColor = 'inherit',
    gapOverride,
    hideCalendarTitle = false,
    monthFontSize = 'text-14',
    rowCount = 2,
    seasonCalendar,
    shadow = false,
    title,
    titleFontSize = 'text-12',
    titleLineHeight,
    titleMarginBottom = 4,
    titleTruncation,
}: SeasonCalendarProps) {
    const isSquare = rowCount === 3;

    return (
        <div>
            {!hideCalendarTitle && (
                <div
                    className={`text-white ${titleFontSize} ${titleLineHeight ?? ''} ${titleTruncation ?? ''}`}
                    style={{
                        color: fontColor === 'inherit' ? 'inherit' : color('white'),
                        marginBottom: titleMarginBottom,
                    }}
                >
                    {title}
                </div>
            )}
            <Columns
                count={rowCount === 3 ? 4 : rowCount === 2 ? 6 : 12}
                gap={(gapOverride ?? rowCount === 3) ? 3 : 4}
                noSpace={rowCount !== 3}
                style={{ width: isSquare ? 93.5 : '100%' }}
            >
                {Array.from({ length: 12 }).map((_, index) => {
                    const monthNumber = index + 1;
                    const monthInfo = seasonCalendar.find(({ month }) => month === monthNumber);
                    const isHighSeason = monthInfo?.seasonType === 'high_season';
                    const isLowSeason = monthInfo?.seasonType === 'low_season';

                    return (
                        <div
                            className={`seasonCalendarMonth flex items-center justify-center text-center leading-14 border rounded-3 overflow-hidden ${isHighSeason ? 'isHighSeason' : isLowSeason ? 'isLowSeason' : ''} ${isSquare ? 'isSquareCalendar' : 'isHorizontalCalendar'} ${monthFontSize}`}
                            key={index}
                            style={{
                                '--strikethroughColor': isSquare ? color('white') : color('nobel'),
                                backgroundColor: isSquare
                                    ? color(isHighSeason ? 'white' : isLowSeason ? 'white-06' : 'black-02')
                                    : color(isHighSeason ? 'check24Blue' : isLowSeason ? 'dustyGray' : 'wildSand'),
                                borderColor: isSquare
                                    ? color('white')
                                    : color(isHighSeason ? 'check24Blue' : isLowSeason ? 'dustyGray' : 'nobel'),
                                boxShadow:
                                    isSquare && isHighSeason && shadow ? `0 0 1px 1px ${color('black-03')}` : 'none',
                                color: isSquare
                                    ? color(isHighSeason ? 'black' : isLowSeason ? 'codGray-085' : 'white')
                                    : color(isHighSeason || isLowSeason ? 'white' : 'nobel'),
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                height: 18,
                                paddingLeft: isSquare ? 0 : 1,
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                width: 22,
                            }}
                        >
                            <style jsx>{`
                                :global(body.body--desktop) .seasonCalendarMonth.isHorizontalCalendar {
                                    font-size: 12px;
                                }
                                .seasonCalendarMonth:not(.isHighSeason):not(.isLowSeason)::after {
                                    content: '.';
                                    transform: translate(-7px, 1px) rotate(-53deg);
                                    background-color: var(--strikethroughColor);
                                    height: 33px;
                                    width: 1px;
                                    color: transparent;
                                }
                            `}</style>
                            {toTwoDigits(monthNumber)}
                        </div>
                    );
                })}
            </Columns>
        </div>
    );
}
